import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Media } from '@capacitor-community/media';
import { isCapacitorNative } from './isCapacitorNative';

export type GalleryThumbnail = {
  id: string;
  previewSrc: string;
};

const RECENT_PHOTO_COUNT = 60;
const THUMBNAIL_SIZE = 320;

function thumbnailSrcFromMedia(media: {
  data?: string;
  identifier: string;
}): string {
  if (media.data?.trim()) {
    const raw = media.data.trim();
    return raw.startsWith('data:') ? raw : `data:image/jpeg;base64,${raw}`;
  }
  return Capacitor.convertFileSrc(media.identifier);
}

async function blobToImageFile(blob: Blob, name: string): Promise<File> {
  const type = blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
  return new File([blob], name, { type });
}

async function fetchFileFromPath(path: string, fallbackName: string): Promise<File | null> {
  const url = path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')
    ? path
    : Capacitor.convertFileSrc(path);
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    const name = path.split('/').pop() || fallbackName;
    return blobToImageFile(blob, name);
  } catch {
    return null;
  }
}

export async function loadRecentPhotoThumbnails(): Promise<GalleryThumbnail[]> {
  if (!isCapacitorNative() || Capacitor.getPlatform() !== 'ios') {
    return [];
  }
  try {
    const { medias } = await Media.getMedias({
      quantity: RECENT_PHOTO_COUNT,
      types: 'photos',
      thumbnailWidth: THUMBNAIL_SIZE,
      thumbnailHeight: THUMBNAIL_SIZE,
      thumbnailQuality: 85,
    });
    return medias.map((media) => ({
      id: media.identifier,
      previewSrc: thumbnailSrcFromMedia(media),
    }));
  } catch {
    return [];
  }
}

export async function pickPhotoFromGallery(): Promise<File | null> {
  if (!isCapacitorNative()) {
    return null;
  }
  try {
    const photo = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.Uri,
      source: CameraSource.Photos,
    });
    const path = photo.webPath ?? photo.path;
    if (!path) {
      return null;
    }
    return fetchFileFromPath(path, 'gallery-photo.jpg');
  } catch {
    return null;
  }
}

export async function resolveGalleryPhotoFile(identifier: string): Promise<File | null> {
  if (!isCapacitorNative()) {
    return null;
  }
  try {
    if (Capacitor.getPlatform() === 'ios') {
      const { path } = await Media.getMediaByIdentifier({ identifier });
      return fetchFileFromPath(path, 'photo.jpg');
    }
    return fetchFileFromPath(identifier, 'photo.jpg');
  } catch {
    return null;
  }
}

export async function capturePhotoFromCamera(): Promise<File | null> {
  if (!isCapacitorNative()) {
    return null;
  }
  try {
    const photo = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.Uri,
      source: CameraSource.Camera,
    });
    const path = photo.webPath ?? photo.path;
    if (!path) {
      return null;
    }
    return fetchFileFromPath(path, 'camera-photo.jpg');
  } catch {
    return null;
  }
}
